import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Chat request interface
export interface ChatRequest {
  message: string;
  conversation_id?: string;
  llm_model_provider: string;
  llm_model: string;
  llm_api_version: string;
  is_anthropic?: boolean;
  web_search_mode?: 'auto' | 'on' | 'off';
  search_domains?: string[];
  sharepoint_enabled?: boolean;
  sharepoint_sites?: string[];
  force_image_generation?: boolean;
  force_deep_research?: boolean;
  latest_generated_image_blob_name?: string;
  persona?: string;
  is_application?: boolean;
  application_id?: string;
  project_id?: string;
}

export const setAuthToken = (token: string) => {
  if (token) {
    // Make sure token is properly formatted with Bearer prefix
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
    console.log('Auth header removed');
  }
};

export const fetchData = async () => {
  try {
    // Make the API call to the configured endpoint
    const endpoint = process.env.REACT_APP_API_ENDPOINT || '/api/data';
    const response = await api.get(endpoint);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching data:', error);
    // Log more specific error details
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    throw error;
  }
};

export const healthCheck = async () => {
  try {
    const healthEndpoint = process.env.REACT_APP_HEALTH_ENDPOINT || '/api/health';
    const response = await api.get(healthEndpoint);
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw error;
  }
};

// Conversation interfaces
export interface ConversationSummary {
  id: string;
  title: string;
  last_message_time: string;
  message_count: number;
  created_at: string;
  is_application: boolean;
  application_id?: string;
  agent_id?: string;
  project_id?: string;
}

export interface ConversationMessage {
  id: string;
  role: string;
  content: string;
  timestamp: string;
  files?: Array<{
    filename: string;
    blob_name: string;
    content_type: string;
    size: number;
  }>;
  parts?: Array<Record<string, any>>;
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string;
  messages: ConversationMessage[];
  created_at: string;
  updated_at: string;
  // Track if conversation was started by an application
  is_application: boolean;
  application_id?: string;
  // Track if conversation is with an agent
  agent_id?: string;
}

// Conversation API functions
export const getConversations = async (): Promise<ConversationSummary[]> => {
  try {
    const response = await api.get('/api/conversations');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

export const getConversation = async (conversationId: string): Promise<Conversation> => {
  try {
    const response = await api.get(`/api/conversations/${conversationId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching conversation:', error);
    throw error;
  }
};

export const createConversation = async (
  title: string,
  isApplication: boolean = false,
  applicationId?: string,
  agentId?: string,
  projectId?: string
): Promise<Conversation> => {
  try {
    const response = await api.post('/api/conversations', {
      title,
      is_application: isApplication,
      application_id: applicationId,
      agent_id: agentId,
      project_id: projectId,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

export const deleteConversation = async (conversationId: string): Promise<void> => {
  try {
    await api.delete(`/api/conversations/${conversationId}`);
  } catch (error: any) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

export const updateConversationTitle = async (conversationId: string, title: string): Promise<void> => {
  try {
    await api.put(`/api/conversations/${conversationId}`, { title });
  } catch (error: any) {
    console.error('Error updating conversation title:', error);
    throw error;
  }
};

export const searchConversations = async (query: string): Promise<ConversationSummary[]> => {
  try {
    if (!query || query.trim() === '') {
      return [];
    }
    const response = await api.get('/api/conversations/search', {
      params: { q: query, limit: 10 }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error searching conversations:', error);
    throw error;
  }
};

// Application interface
export interface Application {
  id: string;
  title: string;
  description: string;
  image_base64: string;
  custom_prompt: string;
  is_active: boolean;
  sort_order: number;
  // New fields for default model configuration
  default_model_provider?: string;
  default_model?: string;
  default_model_api_version?: string;
  // Role required to access this application ("Default" means everyone)
  required_role: string;
  can_use: boolean;
  access?: string;
  typical_audience?: string;
  created_at?: string;
  updated_at?: string;
}

// Application API functions
export const getApplications = async (params?: { search?: string }): Promise<Application[]> => {
  try {
    const response = await api.get('/api/applications', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching applications:', error);
    throw error;
  }
};

// Privacy consent interfaces
export interface PrivacyConsent {
  user_id: string;
  has_consented: boolean;
  consent_timestamp: string | null;
  privacy_policy_version: string;
}

export interface PrivacyConsentResponse {
  success: boolean;
  message: string;
  consent_timestamp: string;
}

// Privacy consent API functions
export const getPrivacyConsent = async (userId: string): Promise<PrivacyConsent> => {
  try {
    const response = await api.get(`/api/privacy/consent/${userId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching privacy consent:', error);
    throw error;
  }
};

export const givePrivacyConsent = async (version?: string): Promise<PrivacyConsentResponse> => {
  try {
    const response = await api.post('/api/privacy/consent', { version });
    return response.data;
  } catch (error: any) {
    console.error('Error giving privacy consent:', error);
    throw error;
  }
};

// Agent interface
export interface Agent {
  id: string;
  title: string;
  description: string;
  img_url: string;
  category: string;
  sort_order: number;
  required_role: string;
  can_use: boolean;
  access?: string;
  typical_audience?: string;
  redirect_url?: string;
  required_connection?: string;
  created_at?: string;
  updated_at?: string;
}

// Agent chat request interface
export interface AgentChatRequest {
  agent_id: string;
  message: string;
  conversation_id?: string;
}

// Agent API functions
export const getAgents = async (params?: { category?: string; search?: string }): Promise<Agent[]> => {
  try {
    const response = await api.get('/api/agents', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching agents:', error);
    throw error;
  }
};

export const getAgentCategories = async (): Promise<string[]> => {
  try {
    const response = await api.get('/api/agents/categories');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching agent categories:', error);
    throw error;
  }
};

export const getAgent = async (agentId: string): Promise<Agent> => {
  try {
    const response = await api.get(`/api/agents/${agentId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching agent:', error);
    throw error;
  }
};

// External account connections (per-user OAuth) required by some agents
export interface ConnectionStatus {
  connected: boolean;
  provider: string;
  scope?: string;
  connected_at?: number;
}

export const getConnectionStatus = async (provider: string): Promise<ConnectionStatus> => {
  const response = await api.get(`/api/connections/${provider}/status`);
  return response.data;
};

export const getConnectionAuthorizeUrl = async (
  provider: string,
  returnUrl?: string
): Promise<string> => {
  const response = await api.get(`/api/connections/${provider}/authorize`, {
    params: returnUrl ? { return_url: returnUrl } : undefined,
  });
  
  return response.data.authorization_url;
};

export const disconnectConnection = async (provider: string): Promise<void> => {
  await api.delete(`/api/connections/${provider}`);
};

// Model option interface - must match the ModelOption interface in ModelSelector.tsx
export interface ModelOption {
  provider: string;
  model: string;
  apiVersion: string;
  description: string;
  isAnthropic?: boolean;
  isAdvanced?: boolean;
  can_use?: boolean;
}

// Model options API function
export const getModelOptions = async (): Promise<ModelOption[]> => {
  try {
    const response = await api.get('/api/models');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching model options:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    throw error;
  }
};

// Quota interfaces
export interface QuotaInfo {
  used: number;
  limit: number;
  resets_at: string;
  is_unlimited: boolean;
}

export const getQuota = async (): Promise<QuotaInfo> => {
  try {
    const response = await api.get('/api/quota');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching quota:', error);
    throw error;
  }
};

export interface SubmitFeedbackRequest {
  conversation_id: string;
  message_index: number;
  feedback_text: string;
  category?: string;
  model_provider?: string;
  model_name?: string;
  model_api_version?: string;
}

export const submitFeedback = async (request: SubmitFeedbackRequest): Promise<{ success: boolean; feedback_id: string }> => {
  try {
    const response = await api.post('/api/feedback', request);
    return response.data;
  } catch (error: any) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

// ── Projects ──

export interface ProjectFile {
  filename: string;
  blob_name: string;
  content_type: string;
  size: number;
  uploaded_at: string;
  file_type: string;
}

export interface ProjectMemory {
  content: string;
  updated_at: string;
  version: number;
}

export interface ProjectSummary {
  id: string;
  name: string;
  icon?: string;
  color?: string;
  memory_enabled: boolean;
  file_count: number;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  user_id: string;
  name: string;
  icon?: string;
  color?: string;
  memory_enabled: boolean;
  memory: ProjectMemory;
  files: ProjectFile[];
  created_at: string;
  updated_at: string;
  deleted?: boolean;
}

export const listProjects = async (): Promise<ProjectSummary[]> => {
  const res = await api.get('/api/projects');
  return res.data;
};

export const getProject = async (projectId: string): Promise<Project> => {
  const res = await api.get(`/api/projects/${projectId}`);
  return res.data;
};

export const createProject = async (payload: {
  name: string;
  icon?: string;
  color?: string;
  memory_enabled?: boolean;
}): Promise<Project> => {
  const res = await api.post('/api/projects', payload);
  return res.data;
};

export const updateProject = async (
  projectId: string,
  payload: { name?: string; icon?: string; color?: string; memory_enabled?: boolean }
): Promise<Project> => {
  const res = await api.put(`/api/projects/${projectId}`, payload);
  return res.data;
};

export const deleteProject = async (projectId: string): Promise<void> => {
  await api.delete(`/api/projects/${projectId}`);
};

export const listProjectConversations = async (projectId: string): Promise<ConversationSummary[]> => {
  const res = await api.get(`/api/projects/${projectId}/conversations`);
  return res.data;
};

export const uploadProjectFile = async (projectId: string, file: File): Promise<Project> => {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`/api/projects/${projectId}/files`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const deleteProjectFile = async (projectId: string, blobName: string): Promise<void> => {
  await api.delete(`/api/projects/${projectId}/files`, {
    params: { blob_name: blobName },
  });
};

// Web search educational nudge
export type WebSearchNudgeAction = 'dismissed' | 'dont_show_again' | 'clicked_retry' | 'clicked_auto';

export const recordWebSearchNudgeAction = async (
  action: WebSearchNudgeAction,
  conversationId?: string
): Promise<void> => {
  try {
    await api.post('/api/nudges/web-search/action', {
      action,
      conversation_id: conversationId,
    });
  } catch (error: any) {
    // Non-critical analytics/state call — never disrupt the chat UX on failure.
    console.error('Error recording web search nudge action:', error);
  }
};

// Help videos
export interface HelpVideo {
  id: string;
  title: string;
  description?: string;
  order: number;
  url: string;
  content_type?: string;
}

export const getHelpVideos = async (): Promise<HelpVideo[]> => {
  const response = await api.get('/api/help/videos');
  return response.data;
};

export default api;

export interface SharePointSite {
  id: string;
  name: string;
  url: string;
}

export const getSharePointSites = async (search?: string): Promise<SharePointSite[]> => {
  const response = await api.get('/api/sharepoint/sites', { params: search ? { search } : undefined });

  return response.data.sites;
};
